import React from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Typography,
} from "@material-ui/core";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import { makeStyles } from "@material-ui/core/styles";
import { UserContext } from "../../contexts/UserContext";
import { TOKEN_KEY, USER_KEY } from "../../CONSTANTS";
import { loggedOutUser } from "../../utils/user";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 2),
  },
  content: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  spaceAfter: {
    marginBottom: 15,
  },
  button: {
    marginTop: theme.spacing(3),
  },
}));

// Where the link emailed by ErasureRequestView (api/views.py) lands. Opening
// the page only checks the link (GET) - nothing is erased until the button
// is pressed (POST), because email scanners and link previews open links on
// their own. Public: the unguessable token is the proof, and it may well be
// opened on a device that isn't signed in.
export const ConfirmErasure = () => {
  const classes = useStyles();
  const location = useLocation();
  const userContext = React.useContext(UserContext);
  const token = new URLSearchParams(location.search).get("token");
  // checking | ready | invalid | working | done
  const [phase, setPhase] = React.useState(token ? "checking" : "invalid");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!token) return;
    axios
      .get(`/api/users/erase/confirm/${encodeURIComponent(token)}/`)
      .then(() => setPhase("ready"))
      .catch(() => setPhase("invalid"));
  }, [token]);

  const erase = () => {
    setPhase("working");
    setError("");
    axios
      .post(`/api/users/erase/confirm/${encodeURIComponent(token)}/`)
      .then(() => {
        // They're erased, so any session on this device is dead - drop it
        // rather than leave a stale signed-in state behind.
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        userContext.setUser(loggedOutUser());
        setPhase("done");
      })
      .catch((err) => {
        const data = err.response && err.response.data;
        if (err.response && err.response.status === 404) {
          setPhase("invalid");
          return;
        }
        setError((data && data.detail) || "Something went wrong - try again.");
        setPhase("ready");
      });
  };

  return (
    <Box className={classes.root} component="main">
      <CssBaseline />
      <Container maxWidth="xs">
        <div className={classes.content}>
          <Avatar className={classes.avatar}>
            <DeleteForeverIcon />
          </Avatar>

          {phase === "checking" && <CircularProgress color="secondary" />}

          {(phase === "ready" || phase === "working") && (
            <>
              <Typography component="h1" variant="h5" className={classes.spaceAfter}>
                Erase your personal data?
              </Typography>
              <Typography variant="body1" color="textSecondary" className={classes.spaceAfter}>
                This permanently removes your name, email and sign-in details, and takes
                you off every team. You won't be able to sign in with this account again.
                It can't be undone.
              </Typography>
              {error && (
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              )}
              <Button
                className={classes.button}
                variant="contained"
                color="secondary"
                onClick={erase}
                disabled={phase === "working"}
              >
                Permanently erase my personal data
              </Button>
              <Button className={classes.button} href="/">
                Cancel
              </Button>
            </>
          )}

          {phase === "invalid" && (
            <>
              <Typography component="h1" variant="h5" className={classes.spaceAfter}>
                This link isn't valid
              </Typography>
              <Typography variant="body1" color="textSecondary" className={classes.spaceAfter}>
                It has expired or has already been used. You can ask for a new one from
                your Account page.
              </Typography>
              <Button className={classes.button} variant="contained" color="primary" href="/">
                Go to PlanPraise
              </Button>
            </>
          )}

          {phase === "done" && (
            <>
              <Typography component="h1" variant="h5" className={classes.spaceAfter}>
                Your personal data has been erased
              </Typography>
              <Typography variant="body1" color="textSecondary" className={classes.spaceAfter}>
                We've also sent a confirmation to your email. Thanks for being part of PlanPraise.
              </Typography>
              <Button className={classes.button} variant="contained" color="primary" href="/">
                Done
              </Button>
            </>
          )}
        </div>
      </Container>
    </Box>
  );
};

export default ConfirmErasure;
